/**
 * Billing data for the /finances cash flow projection.
 *
 * Mirrors the listeners on the Billing & Payables page (salesOrders,
 * qbInvoices, approved changeOrders, billingReadyItems, supplierBills) and
 * reduces them to the inputs `buildCashFlowProjection` needs: unpaid money-in
 * rows (AR), open supplier bills (AP), and leftover job-cost residuals.
 *
 * Degrades gracefully on permission errors — a denied listener leaves its
 * slice empty and surfaces a notice instead of breaking the forecast.
 */

import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../utils/AuthContext';
import { useCRM, useCRMCollections } from '../utils/CRMContext';
import {
  type BillingReadyItem,
  type ReadyToBillRow,
} from '../utils/readyToBill';
import { subscribeBillingReadyItems } from '../utils/readyToBillService';
import type { SupplierBill } from '../utils/supplierBills';
import { subscribeSupplierBills } from '../utils/supplierBillsService';
import { usePaymentLagSettings } from './usePaymentLagSettings';
import type { ChangeOrder, ContractorInvoice, ProposalBuilderData, QBExpense, QBInvoice, SalesOrder } from '../types';
import { subscribeAllContractorInvoices } from '../utils/contractorTimesheetService';
import { filterExpensesForViewer } from '../utils/shortTermOps';
import { qbExpensesProjectPLQuery } from '../utils/qbExpensesQueries';
import {
  buildJobCostForecastSlices,
  contractorInvoiceForecastSlices,
  type JobCostForecastSlice,
} from '../utils/projectJobCostForecast';
import type { PurchaseRequisition } from '../types/purchasing';
import { canViewAllPurchaseRequisitions } from '../utils/purchasingAccess';
import { subscribePurchaseRequisitions } from '../utils/purchasingRequisitions';
import {
  buildIssuedPurchaseForecastSlices,
  type IssuedPurchaseForecastSlice,
} from '../utils/issuedPurchaseForecast';
import {
  buildCompletedProjectIds,
  buildDealIdByProjectId,
  buildForecastProjectMaps,
  buildForecastScheduleTasks,
  buildMoneyInRows,
  buildSupplierTaskEndDates,
  buildTaskNameById,
} from '../utils/forecastPageModel';
import {
  applyQbClearingToSupplierBills,
  qbClearedAtByQbBillId,
  type QbBillPaymentLite,
} from '../utils/qbBillPaymentClearing';

export interface CashFlowProjectionData {
  /** Full money-in queue (portal-aligned schedule); unpaid filtering happens in the projection util. */
  moneyInRows: ReadyToBillRow[];
  salesOrders: Array<SalesOrder & { id: string }>;
  changeOrders: ChangeOrder[];
  supplierBills: SupplierBill[];
  /** Live task end dates — resolves task-anchored supplier payment terms. */
  supplierTaskEndDates: Map<string, Date>;
  /** Task names by id — labels task anchors in the projection breakdown. */
  taskNameById: Map<string, string>;
  /** Timber project display name by project id. */
  projectNameById: Map<string, string>;
  /** Preferred timber project id for a deal. */
  projectIdByDealId: Map<string, string>;
  /** Task → timber project id for timeline deep links. */
  taskProjectIdById: Map<string, string>;
  /** Leftover design / install / freight / travel cash-out, dated from the schedule. */
  jobCostItems: JobCostForecastSlice[];
  /** Issued card POs with no supplier bill yet — open until marked purchased. */
  issuedPurchaseAp: IssuedPurchaseForecastSlice[];
  /** Unpaid contractor install invoices — the dated cash line after the install leftover shrinks. */
  contractorInvoiceAp: ReturnType<typeof contractorInvoiceForecastSlices>;
  /** Signed-proposal builders keyed by deal — timber/CNC node costs for unawarded quotes. */
  proposalsByDeal: Record<string, ProposalBuilderData | null>;
  /** True once the sales-order listener has answered (success or error). */
  loaded: boolean;
  /** Set when AR sources (sales orders / QB invoices) could not be read. */
  arError: string | null;
  /** Set when supplier bills could not be read — projection continues AR-only. */
  apError: string | null;
}

export function useCashFlowProjectionData(enabled: boolean): CashFlowProjectionData {
  const { user } = useAuth();
  const {
    enhancedDeals,
    timberProjects,
    milestones,
    projectTasks,
    taskGroups,
    projectOnboardings,
    crmUsers,
  } = useCRM();
  useCRMCollections(['projectTasks', 'taskGroups', 'projectOnboardings', 'milestones']);
  const { lagSettings } = usePaymentLagSettings();
  const isAdmin = user?.role === 'admin';
  const me = crmUsers.find((u) => u.uid === user?.id);
  const viewAllPurchases = canViewAllPurchaseRequisitions({
    authRole: user?.role,
    designations: me?.designations || user?.designations,
    briefingProfile: me?.executiveBriefing?.profile,
  });

  // Include onboarding-checklist mirror tasks — often the only members of the
  // Production group, which anchors the 40% installment.
  const scheduleTasks = useMemo(
    () => buildForecastScheduleTasks(projectTasks, projectOnboardings, taskGroups),
    [projectTasks, projectOnboardings, taskGroups],
  );

  const [salesOrders, setSalesOrders] = useState<Array<SalesOrder & { id: string }>>([]);
  const [qbInvoices, setQbInvoices] = useState<Array<QBInvoice & { id: string }>>([]);
  const [changeOrders, setChangeOrders] = useState<ChangeOrder[]>([]);
  const [trackedItems, setTrackedItems] = useState<BillingReadyItem[]>([]);
  const [supplierBillsRaw, setSupplierBillsRaw] = useState<SupplierBill[]>([]);
  const [qbBillPayments, setQbBillPayments] = useState<QbBillPaymentLite[]>([]);
  const [purchaseRequisitions, setPurchaseRequisitions] = useState<PurchaseRequisition[]>([]);
  const [contractorInvoices, setContractorInvoices] = useState<ContractorInvoice[]>([]);
  const [proposalsByDeal, setProposalsByDeal] = useState<Record<string, ProposalBuilderData | null>>({});
  const [qbExpenses, setQbExpenses] = useState<Array<QBExpense & { id: string }>>([]);
  const [loaded, setLoaded] = useState(false);
  const [arError, setArError] = useState<string | null>(null);
  const [apError, setApError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const unsub = onSnapshot(
      collection(db, 'salesOrders'),
      (snap) => {
        setSalesOrders(snap.docs.map((d) => ({ id: d.id, ...(d.data() as SalesOrder) })));
        setArError(null);
        setLoaded(true);
      },
      (err) => {
        console.warn('[useCashFlowProjectionData] salesOrders listen error', err);
        setArError('Cannot load billing schedule — cash projection shows burn only.');
        setLoaded(true);
      },
    );
    return unsub;
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    return onSnapshot(
      collection(db, 'qbInvoices'),
      (snap) => setQbInvoices(snap.docs.map((d) => ({ id: d.id, ...(d.data() as QBInvoice) }))),
      (err) => console.warn('[useCashFlowProjectionData] qbInvoices listen error', err),
    );
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    const q = query(collection(db, 'changeOrders'), where('status', '==', 'approved'));
    return onSnapshot(
      q,
      (snap) => setChangeOrders(snap.docs.map((d) => ({ id: d.id, ...(d.data() as ChangeOrder) }))),
      (err) => console.warn('[useCashFlowProjectionData] changeOrders listen error', err),
    );
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    return subscribeBillingReadyItems(
      setTrackedItems,
      (err) => console.warn('[useCashFlowProjectionData] billingReadyItems listen error', err),
    );
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    return subscribeSupplierBills(
      (rows) => {
        setSupplierBillsRaw(rows);
        setApError(null);
      },
      (err) => {
        console.warn('[useCashFlowProjectionData] supplierBills listen error', err);
        setApError('Cannot load supplier bills — cash projection excludes AP.');
      },
    );
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    return onSnapshot(
      collection(db, 'qbBillPayments'),
      (snap) => {
        setQbBillPayments(
          snap.docs.map((d) => {
            const data = d.data() as Record<string, unknown>;
            return {
              txnDate: (data.txnDate as string | null) ?? null,
              payType: (data.payType as string | null) ?? null,
              lines: Array.isArray(data.lines)
                ? (data.lines as Array<{ billQuickbooksId?: string | null }>)
                : [],
            };
          }),
        );
      },
      (err) => console.warn('[useCashFlowProjectionData] qbBillPayments listen error', err),
    );
  }, [enabled]);

  const supplierBills = useMemo(
    () => applyQbClearingToSupplierBills(supplierBillsRaw, qbClearedAtByQbBillId(qbBillPayments)),
    [supplierBillsRaw, qbBillPayments],
  );

  useEffect(() => {
    if (!enabled || !user?.id) return undefined;
    return subscribePurchaseRequisitions(
      { userId: user.id, viewAll: viewAllPurchases },
      setPurchaseRequisitions,
      (err) => console.warn('[useCashFlowProjectionData] purchaseRequisitions listen error', err),
    );
  }, [enabled, user?.id, viewAllPurchases]);

  useEffect(() => {
    if (!enabled) return undefined;
    return subscribeAllContractorInvoices(
      setContractorInvoices,
      (err) => console.warn('[useCashFlowProjectionData] contractorInvoices listen error', err),
    );
  }, [enabled]);

  const proposalDealIds = useMemo(() => {
    const timberDealIds = new Set(
      timberProjects.map((p) => p.dealId).filter((id): id is string => Boolean(id)),
    );
    const ids = new Set<string>();
    for (const deal of enhancedDeals) {
      if (deal.outcome === 'won' && deal.id && timberDealIds.has(deal.id)) ids.add(deal.id);
    }
    return Array.from(ids);
  }, [enhancedDeals, timberProjects]);
  const proposalDealIdsKey = useMemo(() => [...proposalDealIds].sort().join('|'), [proposalDealIds]);

  useEffect(() => {
    if (!enabled) return undefined;
    const ids = new Set(proposalDealIds);
    const unsubs: Array<() => void> = [];
    setProposalsByDeal((prev) => {
      const next: Record<string, ProposalBuilderData | null> = {};
      Object.entries(prev).forEach(([id, val]) => {
        if (ids.has(id)) next[id] = val;
      });
      return next;
    });
    proposalDealIds.forEach((id) => {
      unsubs.push(
        onSnapshot(
          doc(db, 'proposalBuilders', id),
          (snap) => {
            setProposalsByDeal((prev) => ({
              ...prev,
              [id]: snap.exists()
                ? ({ id: snap.id, ...(snap.data() as object) } as ProposalBuilderData)
                : null,
            }));
          },
          (err) => {
            console.warn('[useCashFlowProjectionData] proposalBuilders listen error', id, err);
            setProposalsByDeal((prev) => ({ ...prev, [id]: null }));
          },
        ),
      );
    });
    return () => {
      unsubs.forEach((u) => {
        try {
          u();
        } catch {
          /* ignore */
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, proposalDealIdsKey]);

  useEffect(() => {
    if (!enabled) return undefined;
    return onSnapshot(
      qbExpensesProjectPLQuery(),
      (snap) => {
        const data = snap.docs.map((d) => {
          const raw = d.data();
          return {
            id: d.id,
            ...raw,
            syncedAt: raw.syncedAt?.toDate?.() || new Date(),
            createdAt: raw.createdAt?.toDate?.() || new Date(),
          } as QBExpense & { id: string };
        });
        setQbExpenses(filterExpensesForViewer(data, isAdmin));
      },
      (err) => console.warn('[useCashFlowProjectionData] qbExpenses listen error', err),
    );
  }, [enabled, isAdmin]);

  const moneyInRows = useMemo(
    () =>
      buildMoneyInRows({
        salesOrders,
        qbInvoices,
        deals: enhancedDeals,
        timberProjects,
        milestones,
        changeOrders,
        trackedItems,
        projectTasks: scheduleTasks,
        taskGroups,
        paymentLagSettings: lagSettings,
      }),
    [salesOrders, qbInvoices, enhancedDeals, timberProjects, milestones, changeOrders, trackedItems, scheduleTasks, taskGroups, lagSettings],
  );

  const supplierTaskEndDates = useMemo(
    () => buildSupplierTaskEndDates(scheduleTasks),
    [scheduleTasks],
  );

  const taskNameById = useMemo(
    () => buildTaskNameById(scheduleTasks),
    [scheduleTasks],
  );

  const { projectNameById, projectIdByDealId, taskProjectIdById } = useMemo(
    () => buildForecastProjectMaps({
      enhancedDeals,
      timberProjects,
      scheduleTasks,
      taskGroups,
    }),
    [enhancedDeals, timberProjects, scheduleTasks, taskGroups],
  );

  const completedProjectIds = useMemo(
    () => buildCompletedProjectIds(timberProjects),
    [timberProjects],
  );

  const dealIdByProjectId = useMemo(
    () => buildDealIdByProjectId(timberProjects),
    [timberProjects],
  );

  const contractorInvoiceAp = useMemo(
    () => contractorInvoiceForecastSlices({
      invoices: contractorInvoices,
      dealIdByProjectId,
    }),
    [contractorInvoices, dealIdByProjectId],
  );

  const jobCostItems = useMemo(
    () =>
      buildJobCostForecastSlices({
        deals: enhancedDeals,
        proposalsByDeal,
        changeOrders,
        expenses: qbExpenses,
        supplierBills,
        projectIdByDealId,
        projectNameById,
        completedProjectIds,
        purchaseRequisitions,
        contractorInvoices,
        dealIdByProjectId,
        tasks: scheduleTasks,
        taskGroups,
        milestones,
      }),
    [
      enhancedDeals,
      proposalsByDeal,
      changeOrders,
      qbExpenses,
      supplierBills,
      projectIdByDealId,
      projectNameById,
      completedProjectIds,
      purchaseRequisitions,
      contractorInvoices,
      dealIdByProjectId,
      scheduleTasks,
      taskGroups,
      milestones,
    ],
  );

  const issuedPurchaseAp = useMemo(
    () =>
      buildIssuedPurchaseForecastSlices({
        requisitions: purchaseRequisitions,
        supplierBills,
        qbExpenses,
      }),
    [purchaseRequisitions, supplierBills, qbExpenses],
  );

  return {
    moneyInRows,
    salesOrders,
    changeOrders,
    supplierBills,
    supplierTaskEndDates,
    taskNameById,
    projectNameById,
    projectIdByDealId,
    taskProjectIdById,
    jobCostItems,
    issuedPurchaseAp,
    contractorInvoiceAp,
    proposalsByDeal,
    loaded,
    arError,
    apError,
  };
}
